import React, { useState, useEffect } from 'react';
import { userService } from '../services/api';
import { AvatarUpload } from './AvatarUpload';
import { formatBirthParts } from '../utils/dateHelper';
import { canViewUser } from '../utils/familyTreeHelper';
import { useAuth } from '../context/AuthContext';
import { useFamilyNames, useUsers, useUserDetail } from '../hooks/useSharedData';
import '../styles/modal.scss';

interface UserFormProps {
  user?: any;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const UserFormModal: React.FC<UserFormProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    homeName: '',
    email: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    diedAt: '',
    diedYear: '',
    isAlive: true,
    gender: '',
    spouseName: '',
    spouseBirthDay: '',
    spouseBirthMonth: '',
    spouseBirthYear: '',
    spouseDiedAt: '',
    spouseDiedYear: '',
    spouseIsAlive: true,
    parentId: '',
    familyNameId: '',
    isFamilyAdmin: 'false',
  });

  const [spouseAvatarUrl, setSpouseAvatarUrl] = useState<string | undefined>();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const [eligibleParents, setEligibleParents] = useState<any[]>([]);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();
  const { user: currentUser } = useAuth();
  const { data: familyNames } = useFamilyNames();
  const { data: allUsers } = useUsers();
  const { data: userDetail, loading: userDetailLoading } = useUserDetail(isOpen && user ? user.id : undefined);
  const canSelectFamily = currentUser?.isAdmin === true;
  const canAssignFamilyRole = Boolean(currentUser?.isAdmin || currentUser?.isFamilyAdmin);

  // Fetch full user details when editing
  useEffect(() => {
    setIsLoadingUserData(userDetailLoading);

    if (!isOpen) {
      return;
    }

    if (user?.id) {
      if (userDetail) {
        const userData = userDetail;
        setFormData({
          fullName: userData.fullName || '',
          homeName: userData.homeName || '',
          email: userData.email || '',
          birthDay: userData.birthDay?.toString() || '',
          birthMonth: userData.birthMonth?.toString() || '',
          birthYear: userData.birthYear?.toString() || '',
          diedAt: userData.diedAt || '',
          diedYear: userData.diedYear?.toString() || '',
          isAlive: userData.isAlive !== false,
          gender: userData.gender || '',
          spouseName: userData.spouseName || '',
          spouseBirthDay: userData.spouseBirthDay?.toString() || '',
          spouseBirthMonth: userData.spouseBirthMonth?.toString() || '',
          spouseBirthYear: userData.spouseBirthYear?.toString() || '',
          spouseDiedAt: userData.spouseDiedAt || '',
          spouseDiedYear: userData.spouseDiedYear?.toString() || '',
          spouseIsAlive: userData.spouseIsAlive !== false,
          parentId: userData.parentId?.toString() || '',
          familyNameId: userData.familyNameId?.toString() || '',
          isFamilyAdmin: userData.isFamilyAdmin ? 'true' : 'false',
        });
        setAvatarUrl(userData.imageUrl);
        setSpouseAvatarUrl(userData.spouseImageUrl || undefined);
      }
    } else {
      // Reset form when creating new user
      setFormData({
        fullName: '',
        homeName: '',
        email: '',
        birthDay: '',
        birthMonth: '',
        birthYear: '',
        diedAt: '',
        diedYear: '',
        isAlive: true,
        gender: '',
        spouseName: '',
        spouseBirthDay: '',
        spouseBirthMonth: '',
        spouseBirthYear: '',
        spouseDiedAt: '',
        spouseDiedYear: '',
        spouseIsAlive: true,
        parentId: '',
        familyNameId: canSelectFamily ? '' : String(currentUser?.familyNameId || ''),
        isFamilyAdmin: 'false',
      });
      setAvatarUrl(undefined);
      setSpouseAvatarUrl(undefined);
    }
  }, [isOpen, user, user?.id, userDetail, userDetailLoading, canSelectFamily, currentUser?.familyNameId]);

  useEffect(() => {
    if (!isOpen || !allUsers.length) {
      return;
    }

    const filteredUsers = allUsers.filter((u: any) => u.id !== user?.id && canViewUser(currentUser, u, allUsers));
    setEligibleParents(filteredUsers);
  }, [isOpen, allUsers, user?.id, currentUser]);

  useEffect(() => {
    const filterEligibleParents = () => {
      const visibleUsers = allUsers.filter((u: any) => u.id !== user?.id && canViewUser(currentUser, u, allUsers));

      if (!formData.birthYear) {
        setEligibleParents(visibleUsers);
        return;
      }

      const currentUserDOB = Number(formData.birthYear) * 10000 + Number(formData.birthMonth || 99) * 100 + Number(formData.birthDay || 99);
      const eligible = visibleUsers.filter((u: any) => {
        const parentDOB = u.birthYear ? u.birthYear * 10000 + (u.birthMonth || 99) * 100 + (u.birthDay || 99) : Number.MAX_SAFE_INTEGER;
        return parentDOB < currentUserDOB;
      });
      setEligibleParents(eligible);
    };

    if (isOpen) {
      filterEligibleParents();
    }
  }, [isOpen, formData.birthDay, formData.birthMonth, formData.birthYear, allUsers, user?.id, currentUser]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: e.target instanceof HTMLInputElement && e.target.type === 'checkbox' ? e.target.checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const submitData = {
        fullName: formData.fullName,
        homeName: formData.homeName.trim(),
        email: formData.email,
        birthDay: formData.birthDay ? Number(formData.birthDay) : null,
        birthMonth: formData.birthMonth ? Number(formData.birthMonth) : null,
        birthYear: formData.birthYear ? Number(formData.birthYear) : null,
        clearBirthDay: !formData.birthDay,
        clearBirthMonth: !formData.birthMonth,
        clearBirthYear: !formData.birthYear,
        diedAt: formData.diedAt ? formData.diedAt : undefined,
        clearDiedAt: !formData.diedAt,
        diedYear: formData.diedYear ? Number(formData.diedYear) : null,
        clearDiedYear: !formData.diedYear,
        isAlive: formData.isAlive,
        gender: formData.gender,
        spouseName: formData.spouseName?.trim() || undefined,
        clearSpouseName: !formData.spouseName?.trim(),
        spouseBirthDay: formData.spouseBirthDay ? Number(formData.spouseBirthDay) : null,
        spouseBirthMonth: formData.spouseBirthMonth ? Number(formData.spouseBirthMonth) : null,
        spouseBirthYear: formData.spouseBirthYear ? Number(formData.spouseBirthYear) : null,
        clearSpouseBirthDay: !formData.spouseBirthDay,
        clearSpouseBirthMonth: !formData.spouseBirthMonth,
        clearSpouseBirthYear: !formData.spouseBirthYear,
        spouseDiedAt: formData.spouseDiedAt || undefined,
        clearSpouseDiedAt: !formData.spouseDiedAt,
        spouseDiedYear: formData.spouseDiedYear ? Number(formData.spouseDiedYear) : null,
        clearSpouseDiedYear: !formData.spouseDiedYear,
        spouseIsAlive: formData.spouseIsAlive,
        spouseImageUrl: spouseAvatarUrl || undefined,
        parentId: formData.parentId ? parseInt(formData.parentId) : undefined,
        clearParentId: !formData.parentId,
        familyNameId: parseInt(formData.familyNameId),
        isFamilyAdmin: undefined as boolean | undefined,
      };

      if (canAssignFamilyRole) {
        submitData.isFamilyAdmin = formData.isFamilyAdmin === 'true';
      }

      if (user) {
        await userService.updateUser(user.id, submitData);
      } else {
        await userService.createUser(submitData);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Đã xảy ra lỗi');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{user ? 'Chỉnh sửa người dùng' : 'Thêm người dùng mới'}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {isLoadingUserData ? (
          <div className="modal-body">
            <div className="loading" style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner"></div>
              <p>Đang tải thông tin người dùng...</p>
            </div>
          </div>
        ) : (
          <form className="modal-form" onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger">{error}</div>}

              <div className="form-group">
                <label htmlFor="fullName">Họ và tên *</label>
                <input
                  id="fullName"
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Nhập họ và tên"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="homeName">Tên gọi ở nhà</label>
                <input
                  id="homeName"
                  type="text"
                  name="homeName"
                  value={formData.homeName}
                  onChange={handleChange}
                  placeholder="Nhập tên gọi ở nhà"
                  maxLength={255}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Nhập email"
                  required
                  disabled={!!user}
                />
              </div>

              <div className="form-group">
                <label>Ngày sinh</label>
                <div className="form-inline-fields">
                  <input type="number" name="birthDay" min="1" max="31" placeholder="Ngày" value={formData.birthDay} onChange={handleChange} />
                  <input type="number" name="birthMonth" min="1" max="12" placeholder="Tháng" value={formData.birthMonth} onChange={handleChange} />
                  <input type="number" name="birthYear" min="1" max="2100" placeholder="Năm" value={formData.birthYear} onChange={handleChange} />
                </div>
              </div>

              <label className="form-checkbox">
                <input type="checkbox" name="isAlive" checked={formData.isAlive} onChange={handleChange} />
                <span>Đang sinh sống</span>
              </label>

              {!formData.isAlive && <div className="death-date-fields">
                <div className="form-group">
                  <label htmlFor="diedAt">Ngày mất (âm lịch)</label>
                  <input
                    id="diedAt"
                    type="text"
                    name="diedAt"
                    value={formData.diedAt}
                    placeholder="Ví dụ: 15 tháng 8 năm 2020 âm lịch"
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="diedYear">Năm mất (dương lịch)</label>
                  <input
                    id="diedYear"
                    type="number"
                    name="diedYear"
                    min="1"
                    max="2100"
                    value={formData.diedYear}
                    onChange={handleChange}
                  />
                </div>
              </div>}

              <div className="form-group">
                <label htmlFor="gender">Giới tính *</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                >
                  <option value="">Chọn giới tính</option>
                  <option value="Male">Nam</option>
                  <option value="Female">Nữ</option>
                  <option value="Other">Khác</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="familyNameId">Dòng họ *</label>
                <select
                  id="familyNameId"
                  name="familyNameId"
                  value={formData.familyNameId}
                  onChange={handleChange}
                  required
                  disabled={!canSelectFamily}
                >
                  <option value="">Chọn dòng họ</option>
                  {familyNames.map((familyName: any) => (
                    <option key={familyName.id} value={familyName.id}>
                      {familyName.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="parentId">Cha/Mẹ</label>
                <select
                  id="parentId"
                  name="parentId"
                  value={formData.parentId}
                  onChange={handleChange}
                >
                  <option value="">Không chọn</option>
                  {eligibleParents.map((parent: any) => (
                    <option key={parent.id} value={parent.id}>
                      {parent.fullName} (Sinh: {formatBirthParts(parent.birthDay, parent.birthMonth, parent.birthYear) || 'chưa rõ'})
                    </option>
                  ))}
                </select>
              </div>

              {canAssignFamilyRole && (
                <div className="form-group">
                  <label htmlFor="isFamilyAdmin">Vai trò</label>
                  <select
                    id="isFamilyAdmin"
                    name="isFamilyAdmin"
                    value={formData.isFamilyAdmin}
                    onChange={handleChange}
                  >
                    <option value="false">Người dùng thường</option>
                    <option value="true">Family Admin</option>
                  </select>
                </div>
              )}

              {user && (
                <div className="form-group">
                  <label>Ảnh đại diện</label>
                  <AvatarUpload
                    userId={user.id}
                    currentImageUrl={avatarUrl}
                    onUploadSuccess={(imageUrl) => {
                      setAvatarUrl(imageUrl);
                      setError('');
                    }}
                    onError={(errorMsg) => setError(errorMsg)}
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="spouseName">Tên vợ/chồng</label>
                <input
                  id="spouseName"
                  type="text"
                  name="spouseName"
                  value={formData.spouseName}
                  onChange={handleChange}
                  placeholder="Nhập tên vợ/chồng"
                />
              </div>

              <div className="form-group">
                <label>Năm sinh vợ/chồng</label>
                <div className="form-inline-fields">
                  <input type="number" name="spouseBirthDay" min="1" max="31" placeholder="Ngày" value={formData.spouseBirthDay} onChange={handleChange} />
                  <input type="number" name="spouseBirthMonth" min="1" max="12" placeholder="Tháng" value={formData.spouseBirthMonth} onChange={handleChange} />
                  <input type="number" name="spouseBirthYear" min="1" max="2100" placeholder="Năm" value={formData.spouseBirthYear} onChange={handleChange} />
                </div>
              </div>

              <label className="form-checkbox">
                <input type="checkbox" name="spouseIsAlive" checked={formData.spouseIsAlive} onChange={handleChange} />
                <span>Vợ/chồng đang sinh sống</span>
              </label>

              {!formData.spouseIsAlive && <div className="death-date-fields">
                <div className="form-group">
                  <label htmlFor="spouseDiedAt">Ngày mất vợ/chồng (âm lịch)</label>
                  <input
                    id="spouseDiedAt"
                    type="text"
                    name="spouseDiedAt"
                    value={formData.spouseDiedAt}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="spouseDiedYear">Năm mất (dương lịch)</label>
                  <input
                    id="spouseDiedYear"
                    type="number"
                    name="spouseDiedYear"
                    min="1"
                    max="2100"
                    value={formData.spouseDiedYear}
                    onChange={handleChange}
                  />
                </div>
              </div>}

              <div className="form-group">
                <label>Ảnh vợ/chồng</label>
                <AvatarUpload
                  userId={user?.id ?? currentUser?.id ?? 0}
                  isSpouseImage
                  currentImageUrl={spouseAvatarUrl}
                  onUploadSuccess={(imageUrl) => {
                    setSpouseAvatarUrl(imageUrl);
                    setError('');
                  }}
                  onError={setError}
                />
              </div>

            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-sm"
                onClick={onClose}
                style={{ backgroundColor: '#95a5a6', color: 'white' }}
              >
                Hủy
              </button>
              <button
                type="submit"
                className="btn btn-success btn-sm"
                disabled={isLoading}
              >
                {isLoading ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
